import { useEffect, useMemo, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import { useUser } from "@/hooks/useUser";
import {
  type DashboardOverview,
  type Conversation,
  type VerificationStatus,
} from "@/lib/users-api";
import { type OwnerListing } from "@/lib/listings-api";
import { type EarningsData, type TripListItem } from "@/lib/bookings-api";
import { type OwnerSubscription } from "@/lib/subscriptions-api";
import { Sentry } from "@/lib/sentry";
import {
  DASHBOARD_BOOKING_TABS,
  dashboardQueryOptions,
  useDashboardInvalidator,
  useDashboardUserId,
  useOptimisticListings,
  type BookingsTab,
  type OwnerDashboardStripeStatus,
} from "@/hooks/dashboard-queries";

/**
 * Owner / renter dashboard data hook.
 *
 * Backed entirely by TanStack Query (see `lib/dashboard-queries.ts`)
 * for query-key scoping, automatic deduplication, focus refetch and
 * mutation-driven invalidation. Replaces the previous hand-rolled
 * module-level cache + `fetchInFlight` plumbing.
 *
 * Consumers continue to read from a single combined value and call
 * `refreshX()` methods for back-compat — those refreshers now route
 * to `queryClient.invalidateQueries`, but pages are encouraged to
 * call `useDashboardInvalidator()` directly from mutation handlers
 * so the dashboard refresh travels with the mutation rather than
 * being scattered across page-level callbacks.
 */
export type { OwnerDashboardStripeStatus as StripeStatus };

/**
 * Normalise a dashboard query's error for both the Sentry pipeline and
 * the `errors`/`error` surface consumed by the UI.
 *
 * A 401 on a query that still holds last-good `data` is a transient
 * token-refresh race, not a real failure: the dashboard fires ~12
 * parallel authed reads on load, and on a Safari reload burst the
 * session token can rotate mid-flight so one in-flight GET returns 401
 * even though its sibling reads (and its own earlier fetch) succeeded.
 * The query keeps its cached `data`, so the section still renders — we
 * therefore drop that error from BOTH Sentry and the error surface,
 * otherwise DashboardPage flips to a red banner mid-session and we get
 * paged for nothing (DRIVESHARE_V2-8F). Mirrors the 429 rate-limit
 * suppression in RATE_LIMIT_RETRY / beforeSend.
 *
 * A 401 with NO cached data is a genuinely expired session, so we leave
 * it to surface and drive the normal login/redirect path.
 */
function dashboardSectionError(q: {
  error: unknown;
  data: unknown;
}): Error | null {
  const err = q.error as (Error & { status?: number }) | null;
  if (!err) return null;
  if (err.status === 401 && q.data != null) return null;
  return err;
}

/**
 * Per-section error breakdown so tabs that only render a subset of
 * the dashboard data can decide whether *their* slice failed (e.g.
 * EarningsPage cares about `errors.earnings`, not `errors.listings`).
 */
export interface OwnerDashboardErrors {
  overview: Error | null;
  listings: Error | null;
  earnings: Error | null;
  stripeStatus: Error | null;
  subscription: Error | null;
  conversations: Error | null;
  verification: Error | null;
  bookings: Error | null;
  pendingReviews: Error | null;
}

export interface OwnerDashboardData {
  overview: DashboardOverview | null;
  listings: OwnerListing[];
  earnings: EarningsData | null;
  stripeStatus: OwnerDashboardStripeStatus | null;
  subscription: OwnerSubscription | null;
  conversations: Conversation[];
  verification: VerificationStatus | null;
  bookings: {
    pending: TripListItem[];
    upcoming: TripListItem[];
    past: TripListItem[];
    inProgress: TripListItem[];
  };
  pendingReviewCount: number;
  loading: boolean;
  /** First non-null error across all dashboard queries, or null. */
  error: Error | null;
  /** Per-section error breakdown for fine-grained UI decisions. */
  errors: OwnerDashboardErrors;
  /**
   * True when at least one dashboard query returned a 401 with no
   * cached data — i.e. the session is genuinely expired (the
   * refresh-and-retry in `authFetch` already ran and also failed).
   * Consumers should redirect the user to the sign-in page rather
   * than rendering an error banner.
   */
  sessionExpired: boolean;
  refreshListings: () => Promise<void>;
  refreshBookings: (tab?: BookingsTab) => Promise<void>;
  refreshEarnings: () => Promise<void>;
  refreshConversations: () => Promise<void>;
  refreshVerification: () => Promise<void>;
  /**
   * Task #986: focused refresh for the host's Stripe Connect status —
   * called from `useRefreshOnFocus` so the Approve buttons un-gate
   * automatically once the host finishes Express onboarding in
   * another tab. With TanStack Query this is just a targeted
   * invalidation of the `stripeStatus` slice so we don't refetch
   * everything on every focus event.
   */
  refreshStripeStatus: () => Promise<void>;
  refreshAll: () => Promise<void>;
  setListings: (
    updater: OwnerListing[] | ((prev: OwnerListing[]) => OwnerListing[]),
  ) => void;
}

/**
 * The provider is intentionally a thin pass-through. It used to hold
 * a local `useState` snapshot of the module cache; with TanStack
 * Query the cache lives in the QueryClient (mounted once at the App
 * root) so there is nothing to provide. We keep the component so
 * `App.tsx` doesn't need to know — and so future cross-page
 * coordination has a place to land if it's ever needed.
 */
export function OwnerDashboardProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

// Stable empty fallbacks. Returning a fresh `[]` on every render while a
// query has no data yet gives consumers a new reference each render; any
// effect that depends on it (e.g. MyListingsPage's rental-confirm banner
// hydration) then re-fires every render and can set state in a loop —
// "Maximum update depth exceeded" (Sentry, /dashboard/listings).
const EMPTY_LISTINGS: OwnerListing[] = [];
const EMPTY_CONVERSATIONS: Conversation[] = [];

export function useOwnerDashboard(): OwnerDashboardData {
  const { session } = useUser();
  const token = session?.access_token;
  const userId = useDashboardUserId();
  const invalidator = useDashboardInvalidator();
  const setListingsOptimistic = useOptimisticListings();

  const results = useQueries({
    queries: [
      dashboardQueryOptions.overview(userId, token),
      dashboardQueryOptions.listings(userId, token),
      dashboardQueryOptions.earnings(userId, token),
      dashboardQueryOptions.stripeStatus(userId, token),
      dashboardQueryOptions.subscription(userId, token),
      dashboardQueryOptions.conversations(userId, token),
      dashboardQueryOptions.verification(userId, token),
      dashboardQueryOptions.bookings(userId, token, "pending"),
      dashboardQueryOptions.bookings(userId, token, "upcoming"),
      dashboardQueryOptions.bookings(userId, token, "past"),
      dashboardQueryOptions.bookings(userId, token, "in_progress"),
      dashboardQueryOptions.pendingReviews(userId, token),
    ],
  });

  const [
    overviewQ,
    listingsQ,
    earningsQ,
    stripeQ,
    subscriptionQ,
    conversationsQ,
    verificationQ,
    bookingsPendingQ,
    bookingsUpcomingQ,
    bookingsPastQ,
    bookingsInProgressQ,
    pendingReviewsQ,
  ] = results;

  // Surface genuine fetch failures to Sentry. We deliberately do NOT
  // throw or bubble these into the UI — the page falls back to
  // empty/null and stays usable. Transient token-refresh 401s (a query
  // that 401s while still holding last-good `data`, see
  // `dashboardSectionError`) are dropped here too so they don't page us
  // (DRIVESHARE_V2-8F).
  //
  // Genuine session-expiry 401s (no cached data, refresh also failed)
  // are also excluded from Sentry — they are expected auth lifecycle
  // events, not bugs. The `sessionExpired` flag drives a login redirect
  // so the user gets a clear "please sign in again" path.
  useEffect(() => {
    for (const q of results) {
      const err = dashboardSectionError(q);
      if (!err) continue;
      if ((err as Error & { status?: number }).status === 401) {
        // Expected auth expiry — handled via `sessionExpired` redirect.
        // Do NOT send to Sentry.
        continue;
      }
      if ((err as Error & { code?: string }).code === "BOOKING_API_TIMEOUT") {
        // bookings-api already emits the canonical diagnostic message with
        // its route/network classification. Capturing this same error from
        // the query observer would create a second Sentry issue.
        continue;
      }
      // eslint-disable-next-line no-console
      console.error("[OwnerDashboard] query error:", err);
      Sentry.captureException(err);
    }
    // We intentionally include only the error references; including the
    // full `results` array would re-fire on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    overviewQ.error,
    listingsQ.error,
    earningsQ.error,
    stripeQ.error,
    subscriptionQ.error,
    conversationsQ.error,
    verificationQ.error,
    bookingsPendingQ.error,
    bookingsUpcomingQ.error,
    bookingsPastQ.error,
    bookingsInProgressQ.error,
    pendingReviewsQ.error,
  ]);

  // `loading = some query still doing its first fetch`. When the user
  // is logged out (no token / no userId) every query is `enabled:
  // false` and stays `pending` forever, so guard with the auth check
  // — otherwise the dashboard would spin on the login redirect path.
  const loading = useMemo(
    () => !!token && !!userId && results.some((q) => q.isPending),
    // results identities change every render but the .isPending bools
    // are what matters; depend on the enabling inputs and let React
    // recompute every render (cheap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, userId, ...results.map((q) => q.isPending)],
  );

  const bookings = useMemo(
    () => ({
      pending: bookingsPendingQ.data ?? [],
      upcoming: bookingsUpcomingQ.data ?? [],
      past: bookingsPastQ.data ?? [],
      inProgress: bookingsInProgressQ.data ?? [],
    }),
    [bookingsPendingQ.data, bookingsUpcomingQ.data, bookingsPastQ.data, bookingsInProgressQ.data],
  );

  const pendingReviewCount = Array.isArray(pendingReviewsQ.data)
    ? pendingReviewsQ.data.length
    : 0;

  // True when any section has a 401 error that `dashboardSectionError`
  // surfaces (i.e. no stale cached data to fall back on) — meaning the
  // refresh-and-retry in `authFetch` already ran and still failed. The
  // session is genuinely expired; the UI should redirect to sign-in.
  const sessionExpired = useMemo(
    () =>
      results.some((q) => {
        const err = dashboardSectionError(q) as (Error & { status?: number }) | null;
        return err?.status === 401;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      overviewQ.error, overviewQ.data,
      listingsQ.error, listingsQ.data,
      earningsQ.error, earningsQ.data,
      stripeQ.error, stripeQ.data,
      subscriptionQ.error, subscriptionQ.data,
      conversationsQ.error, conversationsQ.data,
      verificationQ.error, verificationQ.data,
      bookingsPendingQ.error, bookingsPendingQ.data,
      bookingsUpcomingQ.error, bookingsUpcomingQ.data,
      bookingsPastQ.error, bookingsPastQ.data,
      bookingsInProgressQ.error, bookingsInProgressQ.data,
      pendingReviewsQ.error, pendingReviewsQ.data,
    ],
  );

  const errors: OwnerDashboardErrors = {
    overview: dashboardSectionError(overviewQ),
    listings: dashboardSectionError(listingsQ),
    earnings: dashboardSectionError(earningsQ),
    stripeStatus: dashboardSectionError(stripeQ),
    subscription: dashboardSectionError(subscriptionQ),
    conversations: dashboardSectionError(conversationsQ),
    verification: dashboardSectionError(verificationQ),
    bookings:
      dashboardSectionError(bookingsPendingQ) ??
      dashboardSectionError(bookingsUpcomingQ) ??
      dashboardSectionError(bookingsPastQ) ??
      dashboardSectionError(bookingsInProgressQ),
    pendingReviews: dashboardSectionError(pendingReviewsQ),
  };
  // First non-null error across all sections — handy for the "did the
  // overall dashboard load?" decision in `DashboardPage`. Per-section
  // tabs should prefer their own slice of `errors`.
  const aggregateError =
    errors.overview ??
    errors.listings ??
    errors.bookings ??
    errors.earnings ??
    errors.stripeStatus ??
    errors.subscription ??
    errors.conversations ??
    errors.verification ??
    errors.pendingReviews ??
    null;

  return {
    overview: overviewQ.data ?? null,
    listings: listingsQ.data?.listings ?? EMPTY_LISTINGS,
    earnings: earningsQ.data ?? null,
    stripeStatus: stripeQ.data ?? null,
    subscription: subscriptionQ.data?.subscription ?? null,
    conversations: conversationsQ.data ?? EMPTY_CONVERSATIONS,
    verification: verificationQ.data ?? null,
    bookings,
    pendingReviewCount,
    loading,
    error: aggregateError,
    errors,
    sessionExpired,
    refreshListings: async () => {
      await invalidator.invalidateListings();
    },
    refreshBookings: async (tab) => {
      if (tab) {
        await invalidator.invalidateBookingsTab(tab);
      } else {
        await Promise.all(
          DASHBOARD_BOOKING_TABS.map((t) => invalidator.invalidateBookingsTab(t)),
        );
      }
    },
    refreshEarnings: async () => {
      await Promise.all([
        invalidator.invalidateEarnings(),
        invalidator.invalidateStripeStatus(),
      ]);
    },
    refreshConversations: async () => {
      await invalidator.invalidateConversations();
    },
    refreshVerification: async () => {
      await invalidator.invalidateVerification();
    },
    // Task #986: targeted Stripe Connect status refresh used by
    // OwnerBookingsPage's `useRefreshOnFocus`. We invalidate just the
    // `stripeStatus` slice so a window-focus event doesn't refetch
    // every dashboard query.
    refreshStripeStatus: async () => {
      await invalidator.invalidateStripeStatus();
    },
    refreshAll: async () => {
      await invalidator.invalidateAll();
    },
    setListings: setListingsOptimistic,
  };
}
